import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import API from "../api";
import { Card, Input, Button, Toast, SectionHeader } from "../components/UI";

const TWO_WHEELER = ["Hero","Honda","TVS","Bajaj","Yamaha","Suzuki","Royal Enfield","KTM","Ola Electric","Ather Energy","Revolt"];
const FOUR_WHEELER = ["Maruti Suzuki","Hyundai","Tata Motors","Mahindra","Kia","Toyota","MG Motor","Skoda","Volkswagen","Ford","Renault","Nissan"];

export default function EditClient() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [connectLoading, setConnectLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [form, setForm] = useState({
    name: "", brand: "", city: "", botType: "sales",
    whatsappPhoneId: "", whatsappToken: "",
    whatsappWabaId: "",
    googleSheetUrl: "",
    botProfile_address: "", botProfile_phone: "",
    botProfile_hours: "", botProfile_offers: "",
    botProfile_extraInfo: "",
    notify_email_enabled: false, notify_email: "",
    notify_whatsapp_enabled: false, notify_whatsapp: "",
    notify_telegram_enabled: false, notify_telegram_chatId: "",
  });

  useEffect(() => {
    API.get(`/client/${id}`).then(r => {
      const c = r.data;
      setForm({
        name: c.name || "",
        brand: c.brand || "",
        city: c.city || "",
        botType: c.botType || "sales",
        whatsappPhoneId: c.whatsapp?.phoneId || "",
        whatsappToken: c.whatsapp?.token || "",
        whatsappWabaId: c.whatsapp?.wabaId || "",
        googleSheetUrl: c.googleSheetUrl || "",
        botProfile_address: c.botProfile?.address || "",
        botProfile_phone: c.botProfile?.phone || "",
        botProfile_hours: c.botProfile?.hours || "",
        botProfile_offers: c.botProfile?.offers || "",
        botProfile_extraInfo: c.botProfile?.extraInfo || "",
        notify_email_enabled: !!c.notifications?.email,
        notify_email: c.notifications?.email || "",
        notify_whatsapp_enabled: !!c.notifications?.whatsapp,
        notify_whatsapp: c.notifications?.whatsapp || "",
        notify_telegram_enabled: !!(c.notifications?.telegram?.chatId),
        notify_telegram_chatId: c.notifications?.telegram?.chatId || "",
      });
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, [id]);

  const handleChange = e => setForm(p => ({ ...p, [e.target.name]: e.target.value }));
  const handleCheck = e => setForm(p => ({ ...p, [e.target.name]: e.target.checked }));

  const handleWhatsAppConnect = async () => {
    setConnectLoading(true);
    try {
      const res = await API.get(`/auth/onboard-url/${id}`);
      window.location.href = res.data.onboardUrl;
    } catch (err) {
      setToast({ message: "WhatsApp connect failed. Try again.", type: "error" });
      setConnectLoading(false);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      await API.put(`/client/${id}`, {
        name: form.name,
        brand: form.brand,
        city: form.city,
        botType: form.botType,
        whatsapp: {
          phoneId: form.whatsappPhoneId,
          token: form.whatsappToken,
        },
        googleSheetUrl: form.googleSheetUrl,
        botProfile: {
          address: form.botProfile_address,
          phone: form.botProfile_phone,
          hours: form.botProfile_hours,
          offers: form.botProfile_offers,
          extraInfo: form.botProfile_extraInfo,
        },
        notifications: {
          email: form.notify_email_enabled ? form.notify_email : "",
          whatsapp: form.notify_whatsapp_enabled ? form.notify_whatsapp : "",
          telegram: {
            chatId: form.notify_telegram_enabled ? form.notify_telegram_chatId : "",
          }
        }
      });
      setToast({ message: "Client updated!", type: "success" });
      setTimeout(() => navigate("/app/clients"), 1200);
    } catch (err) {
      setToast({ message: err.response?.data?.error || "Failed", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: 40, color: "var(--text-muted)" }}>Loading...</div>;

  const personal = form.brand === "Personal";
  const isConnected = !!(form.whatsappWabaId && form.whatsappPhoneId && form.whatsappPhoneId !== "MOCK_PHONE_ID");

  return (
    <div className="fade-in" style={{ maxWidth: 560 }}>
      <SectionHeader title="Edit Client" subtitle={`Editing: ${form.name}`} />
      <Card>
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>

          {/* ── Basic Info ── */}
          <div style={s.sectionLabel}>Basic Info</div>
          <Input label="Client Name" name="name" value={form.name} onChange={handleChange} required />

          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <label style={s.label}>Brand <span style={{ color: "var(--accent-red)" }}>*</span></label>
            <select name="brand" value={form.brand} onChange={handleChange} required style={s.select}>
              <option value="">-- Select --</option>
              <optgroup label="── Personal Bot">
                <option value="Personal">🤖 Personal (AI Sales Bot)</option>
              </optgroup>
              <optgroup label="── 2-Wheeler Brands">
                {TWO_WHEELER.map(b => <option key={b} value={b}>{b}</option>)}
              </optgroup>
              <optgroup label="── 4-Wheeler Brands">
                {FOUR_WHEELER.map(b => <option key={b} value={b}>{b}</option>)}
              </optgroup>
              <optgroup label="── Other">
                <option value="Other">Other</option>
              </optgroup>
            </select>
          </div>

          {!personal && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={s.label}>Bot Type</label>
              <select name="botType" value={form.botType} onChange={handleChange} style={s.select}>
                <option value="sales">🏍️ Sales Bot</option>
                <option value="service">🔧 Service Bot</option>
              </select>
              <div style={{
                fontSize: 12, padding: "8px 12px", borderRadius: "var(--radius)", lineHeight: 1.6,
                background: form.botType === "service" ? "rgba(255,149,0,0.07)" : "rgba(77,159,255,0.07)",
                border: `1px solid ${form.botType === "service" ? "rgba(255,149,0,0.2)" : "rgba(77,159,255,0.2)"}`,
                color: form.botType === "service" ? "var(--accent-orange)" : "var(--accent-blue)",
              }}>
                {form.botType === "service"
                  ? "🔧 Service Bot — booking, queries, staff alert"
                  : "🏍️ Sales Bot — bike details, pricing, lead capture"}
              </div>
            </div>
          )}

          <Input label="City" name="city" value={form.city} onChange={handleChange} />

          {/* ── WhatsApp Connection ── */}
          <div style={{ height: 1, background: "var(--border)" }} />
          <div style={s.sectionLabel}>WhatsApp Connection</div>

          {isConnected ? (
            <div style={s.connectedBox}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={s.connectedDot} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--accent-green, #34c759)" }}>
                    WhatsApp Connected
                  </div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
                    WABA: {form.whatsappWabaId} · Phone ID: {form.whatsappPhoneId}
                  </div>
                </div>
              </div>
              <button type="button" onClick={handleWhatsAppConnect} style={s.reconnectBtn}>
                Reconnect
              </button>
            </div>
          ) : (
            <div style={s.connectBox}>
              <div style={{ fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.6, marginBottom: 12 }}>
                📱 <strong>Ek click mein connect karo</strong> — dealer ka WhatsApp Business account automatically link ho jayega.
              </div>
              <button
                type="button"
                onClick={handleWhatsAppConnect}
                disabled={connectLoading}
                style={s.connectBtn}
              >
                {connectLoading
                  ? <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={s.spinner} /> Redirecting...
                    </span>
                  : "🔗 Connect WhatsApp Business"}
              </button>
            </div>
          )}

          <details>
            <summary style={{ fontSize: 11, color: "var(--text-muted)", cursor: "pointer", userSelect: "none" }}>
              ⚙️ Manual credentials (advanced)
            </summary>
            <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 12 }}>
              <Input label="Phone Number ID" name="whatsappPhoneId" value={form.whatsappPhoneId} onChange={handleChange} />
              <Input label="Access Token" name="whatsappToken" value={form.whatsappToken} onChange={handleChange} type="password" />
            </div>
          </details>

          {/* ── Google Sheet ── */}
          <div style={{ height: 1, background: "var(--border)" }} />
          <div style={s.sectionLabel}>Google Sheet {personal ? "" : "(Optional)"}</div>
          <Input label="Webhook URL" name="googleSheetUrl" value={form.googleSheetUrl} onChange={handleChange} placeholder="Apps Script Web App URL" />

          {/* ── Bot Profile ── */}
          <div style={{ height: 1, background: "var(--border)" }} />
          <div style={s.sectionLabel}>Bot Profile</div>
          <Input label="Address" name="botProfile_address" value={form.botProfile_address} onChange={handleChange} placeholder="Showroom / Service center address" />
          <Input label="Contact Number" name="botProfile_phone" value={form.botProfile_phone} onChange={handleChange} placeholder="9876543210" />
          <Input label="Business Hours" name="botProfile_hours" value={form.botProfile_hours} onChange={handleChange} placeholder="Mon-Sat 9AM-6PM" />
          <Input label="Current Offers" name="botProfile_offers" value={form.botProfile_offers} onChange={handleChange} placeholder="Free first service, AMC plans etc." />
          <Input label="Extra Info" name="botProfile_extraInfo" value={form.botProfile_extraInfo} onChange={handleChange} placeholder="Jo bhi bot ko pata hona chahiye" />

          {/* ── Notifications ── */}
          <div style={{ height: 1, background: "var(--border)" }} />
          <div style={s.sectionLabel}>Notifications</div>
          <div style={s.infoBox}>📢 Hot lead aane pe alert — jo chahiye enable karo.</div>

          <div style={s.notifRow}>
            <label style={s.checkLabel}>
              <input type="checkbox" name="notify_email_enabled" checked={form.notify_email_enabled} onChange={handleCheck} style={s.checkbox} />
              <span>📧 Email Alert</span>
            </label>
            {form.notify_email_enabled && (
              <Input name="notify_email" value={form.notify_email} onChange={handleChange} placeholder="dealer@gmail.com" />
            )}
          </div>

          <div style={s.notifRow}>
            <label style={s.checkLabel}>
              <input type="checkbox" name="notify_whatsapp_enabled" checked={form.notify_whatsapp_enabled} onChange={handleCheck} style={s.checkbox} />
              <span>📱 WhatsApp Alert</span>
            </label>
            {form.notify_whatsapp_enabled && (
              <Input name="notify_whatsapp" value={form.notify_whatsapp} onChange={handleChange} placeholder="919876543210" />
            )}
          </div>

          <div style={s.notifRow}>
            <label style={s.checkLabel}>
              <input type="checkbox" name="notify_telegram_enabled" checked={form.notify_telegram_enabled} onChange={handleCheck} style={s.checkbox} />
              <span>✈️ Telegram Alert</span>
            </label>
            {form.notify_telegram_enabled && (
              <>
                <div style={s.telegramInfo}>
                  📱 <b>Steps:</b><br />
                  1. Telegram pe <code style={{ background: "rgba(255,255,255,0.08)", padding: "1px 6px", borderRadius: 4 }}>@Wappalert_bot</code> open karo<br />
                  2. <code style={{ background: "rgba(255,255,255,0.08)", padding: "1px 6px", borderRadius: 4 }}>/start</code> bhejo<br />
                  3. Chat ID copy karo aur neeche paste karo
                </div>
                <Input
                  name="notify_telegram_chatId"
                  value={form.notify_telegram_chatId}
                  onChange={handleChange}
                  placeholder="Chat ID — @Wappalert_bot se lo"
                />
              </>
            )}
          </div>

          {/* ── Actions ── */}
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 8 }}>
            <Button variant="secondary" onClick={() => navigate("/app/clients")} type="button">Cancel</Button>
            <Button type="submit" disabled={saving}>{saving ? "Saving..." : "✓ Save Changes"}</Button>
          </div>

        </form>
      </Card>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

const s = {
  sectionLabel: {
    fontSize: 11, fontWeight: 700, color: "var(--text-muted)",
    textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: "var(--font-mono)"
  },
  label: {
    fontSize: 12, color: "var(--text-secondary)", fontWeight: 500,
    textTransform: "uppercase", letterSpacing: "0.05em"
  },
  select: {
    background: "var(--bg-elevated)", border: "1px solid var(--border)",
    borderRadius: "var(--radius)", padding: "10px 14px",
    color: "var(--text-primary)", fontSize: 14,
    fontFamily: "var(--font-body)", outline: "none", width: "100%", cursor: "pointer"
  },
  infoBox: {
    fontSize: 12, color: "var(--accent-blue)", background: "rgba(77,159,255,0.08)",
    border: "1px solid rgba(77,159,255,0.2)", borderRadius: "var(--radius)",
    padding: "8px 12px", lineHeight: 1.6
  },
  telegramInfo: {
    fontSize: 12, color: "var(--text-secondary)", background: "rgba(37,211,102,0.06)",
    border: "1px solid rgba(37,211,102,0.2)", borderRadius: "var(--radius)",
    padding: "10px 12px", lineHeight: 1.9
  },
  notifRow: {
    display: "flex", flexDirection: "column", gap: 8,
    background: "var(--bg-elevated)", border: "1px solid var(--border)",
    borderRadius: "var(--radius)", padding: "12px"
  },
  checkLabel: {
    display: "flex", alignItems: "center", gap: 10,
    cursor: "pointer", fontSize: 13, color: "var(--text-primary)", fontWeight: 500
  },
  checkbox: { width: 16, height: 16, cursor: "pointer", accentColor: "var(--accent)" },
  connectBox: {
    padding: "16px", borderRadius: "var(--radius)",
    background: "rgba(77,159,255,0.06)",
    border: "1px solid rgba(77,159,255,0.2)",
  },
  connectBtn: {
    width: "100%", padding: "11px 20px",
    borderRadius: "var(--radius)",
    background: "var(--accent, #4d9fff)",
    color: "#fff", border: "none",
    cursor: "pointer", fontSize: 14,
    fontWeight: 600, fontFamily: "var(--font-body)",
    display: "flex", alignItems: "center",
    justifyContent: "center", gap: 8,
    transition: "opacity 0.15s",
  },
  connectedBox: {
    padding: "12px 16px", borderRadius: "var(--radius)",
    background: "rgba(52,199,89,0.07)",
    border: "1px solid rgba(52,199,89,0.25)",
    display: "flex", alignItems: "center",
    justifyContent: "space-between", gap: 12,
  },
  connectedDot: {
    width: 8, height: 8, borderRadius: "50%",
    background: "var(--accent-green, #34c759)",
    boxShadow: "0 0 6px rgba(52,199,89,0.6)",
    flexShrink: 0,
  },
  reconnectBtn: {
    padding: "6px 14px", borderRadius: "var(--radius)",
    background: "rgba(52,199,89,0.12)",
    color: "var(--accent-green, #34c759)",
    border: "1px solid rgba(52,199,89,0.25)",
    cursor: "pointer", fontSize: 12,
    fontWeight: 600, fontFamily: "var(--font-body)",
    flexShrink: 0,
  },
  spinner: {
    width: 14, height: 14,
    border: "2px solid rgba(255,255,255,0.3)",
    borderTop: "2px solid #fff",
    borderRadius: "50%",
    display: "inline-block",
    animation: "spin 0.7s linear infinite",
  },
};